import { observable } from "@legendapp/state";

import { trpc } from "~/libs/trpc.lib";
import { user$ } from "~/stores/auth";

/**
 * One row in the **Shared with me** sidebar — boards the current user has
 * been invited to (Phase 6 `board_members`). User-scoped, not org-scoped:
 * lists boards across orgs without requiring `activeOrganization$`.
 */
export interface SharedBoardEntry {
  boardId: string;
  boardName: string | null;
  boardAvatar: unknown;
  organizationId: string;
  organizationName: string;
  organizationLogo: string | null;
}

interface SharedBoardsState {
  loading: boolean;
  loaded: boolean;
  items: SharedBoardEntry[];
}

export const sharedBoards$ = observable<SharedBoardsState>({
  loading: false,
  loaded: false,
  items: [],
});

let inFlight: Promise<void> | null = null;

/**
 * Fetches the current user's shared boards. Idempotent — concurrent calls
 * share a single in-flight request. Re-fetches on subsequent calls so the
 * sidebar can refresh after accepting an invite.
 */
export const refreshSharedBoards = async () => {
  if (inFlight) return inFlight;
  if (!user$.id.get()) {
    sharedBoards$.set({ loading: false, loaded: true, items: [] });
    return;
  }

  sharedBoards$.loading.set(true);
  inFlight = (async () => {
    try {
      const items = await trpc.sharedBoards.listForUser.query();
      sharedBoards$.set({ loading: false, loaded: true, items });
    } catch {
      sharedBoards$.set({ loading: false, loaded: true, items: [] });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

// Refresh whenever the signed-in user changes.
user$.id.onChange(({ value }) => {
  if (value) {
    refreshSharedBoards();
  } else {
    sharedBoards$.set({ loading: false, loaded: false, items: [] });
  }
});
